import { Rest } from '@abp/ng.core';
import { Injectable, inject } from '@angular/core';
import {
  type GetIdentityUsersInput,
  type IdentityUserDto,
  IdentityUserService,
} from '@volo/abp.ng.identity/proxy';
import { forkJoin, map, of } from 'rxjs';

type LookupIdentityUser = IdentityUserDto & { displayName: string };
type LookupIdentityUserListInput = GetIdentityUsersInput & {
  displayName?: string;
  sorting?: string;
};

@Injectable({ providedIn: 'root' })
export class AuditUserLookupService {
  private readonly identityUserService = inject(IdentityUserService);

  get = (id: string, config?: Partial<Rest.Config>) =>
    this.identityUserService.get(id, config).pipe(map(this.toLookupItem));

  getList = (input: LookupIdentityUserListInput, config?: Partial<Rest.Config>) =>
    this.identityUserService.getList(this.normalizeListInput(input), config).pipe(
      map(response => ({
        ...response,
        items: (response.items ?? []).map(this.toLookupItem),
      }))
    );

  getListByIds = (
    input: { ids: Array<string | number> | string },
    config?: Partial<Rest.Config>
  ) => {
    const ids = this.parseIds(input.ids);

    if (!ids.length) {
      return of([] as LookupIdentityUser[]);
    }

    return forkJoin(ids.map(id => this.identityUserService.get(id, config))).pipe(
      map(items => items.map(this.toLookupItem))
    );
  };

  private normalizeListInput(input: LookupIdentityUserListInput): GetIdentityUsersInput {
    const normalized: LookupIdentityUserListInput = { ...input };

    if (normalized.displayName && !normalized.filter) {
      normalized.filter = normalized.displayName;
    }

    delete normalized.displayName;

    if (normalized.sorting) {
      normalized.sorting = normalized.sorting.replaceAll(/\bdisplayName\b/g, 'name');
    }

    return normalized;
  }

  private parseIds(ids: Array<string | number> | string): string[] {
    if (Array.isArray(ids)) {
      return ids.map(String).filter(Boolean);
    }

    return ids
      .split(',')
      .map(id => id.trim())
      .filter(Boolean);
  }

  private readonly toLookupItem = (user: IdentityUserDto): LookupIdentityUser => ({
    ...user,
    displayName: this.getDisplayName(user),
  });

  private getDisplayName(user: IdentityUserDto): string {
    const fullName = [user.name?.trim(), user.surname?.trim()].filter(Boolean).join(' ');
    const userName = user.userName?.trim();
    return fullName && userName
      ? `${fullName} (${userName})`
      : fullName || userName || user.id || '';
  }
}
